// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Image, ImageStyle, Pressable, StyleProp, StyleSheet, Text, TextStyle, ViewStyle } from 'react-native';
import { useGlobalSearchParams } from 'expo-router';

import { getAssetImageSource } from '@/services/assetImageSource';
import appColors from '@/theme/colors';

type GoffyButtonProps = {
  label: string;
  onPress?: () => void;
  disabled?: boolean;
  size?: 'default' | 'compact';
  visualStyle?: 'blue' | 'image';
  tone?: 'dark' | 'accent';
  oldButtonStyle?: StyleProp<ViewStyle>;
  oldTextStyle?: StyleProp<TextStyle>;
  oldDisabledStyle?: StyleProp<ViewStyle>;
  oldDisabledTextStyle?: StyleProp<TextStyle>;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  imageStyle?: StyleProp<ImageStyle>;
  accessibilityLabel?: string;
};

export default function GoffyButton({
  label,
  onPress,
  disabled = false,
  size = 'default',
  visualStyle,
  tone = 'dark',
  oldButtonStyle,
  oldTextStyle,
  oldDisabledStyle,
  oldDisabledTextStyle,
  style,
  textStyle,
  imageStyle,
  accessibilityLabel,
}: GoffyButtonProps) {
  const { buttons } = useGlobalSearchParams<{ buttons?: string | string[] }>();
  const buttonMode = Array.isArray(buttons) ? buttons[0] : buttons;
  // Visual mode is explicit first; legacy query override is low-priority compatibility.
  const effectiveVisualStyle: 'blue' | 'image' = visualStyle
    ?? (buttonMode === 'image' ? 'image' : 'blue');
  const buttonSizeStyle = size === 'compact' ? styles.compactButtonSize : styles.defaultButtonSize;
  const largeButtonImage = getAssetImageSource('images/large_button.png', 'button', undefined, { width: 300, bucketStep: 1 });

  if (effectiveVisualStyle === 'blue') {
    const toneButtonStyle = tone === 'accent' ? styles.oldButtonAccent : styles.oldButtonDark;
    const toneTextStyle = tone === 'accent' ? styles.oldButtonTextAccent : styles.oldButtonTextDark;
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        onPress={onPress}
        disabled={disabled}
        style={({ pressed }) => [
          styles.oldButtonBase,
          buttonSizeStyle,
          oldButtonStyle,
          style,
          toneButtonStyle,
          pressed && !disabled && styles.oldButtonPressed,
          disabled && oldDisabledStyle,
          disabled && styles.oldButtonDisabled,
        ]}
      >
        <Text
          style={[
            oldTextStyle,
            textStyle,
            styles.oldButtonText,
            toneTextStyle,
            disabled && oldDisabledTextStyle,
            disabled && styles.oldButtonTextDisabled,
          ]}
        >
          {label}
        </Text>
      </Pressable>
    );
  }

  return (
    <Pressable
      style={[styles.imageButton, buttonSizeStyle, style, disabled && styles.imageButtonDisabled]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
    >
      <Image source={largeButtonImage} style={[styles.imageButtonImage, imageStyle]} resizeMode="contain" accessible={false} />
      <Text style={[styles.imageButtonText, textStyle, disabled && styles.imageButtonTextDisabled]}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  oldButtonBase: {
    width: 220,
    height: 56,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 0,
    paddingHorizontal: 18,
  },
  oldButtonDark: {
    backgroundColor: '#06210E',
    borderColor: '#1C4023',
  },
  oldButtonAccent: {
    backgroundColor: appColors.accent.primary,
    borderColor: appColors.accent.primary,
  },
  oldButtonPressed: {
    opacity: 0.88,
    transform: [{ translateY: 1 }],
  },
  oldButtonDisabled: {
    opacity: 0.48,
  },
  oldButtonText: {
    fontWeight: '800',
    fontSize: 12,
    letterSpacing: 2.1,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  oldButtonTextDark: {
    color: appColors.text.secondary,
  },
  oldButtonTextAccent: {
    color: appColors.background.app,
  },
  oldButtonTextDisabled: {
    color: appColors.text.muted,
  },
  imageButton: {
    width: 220,
    height: 62,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: 'transparent',
  },
  imageButtonDisabled: {
    opacity: 0.6,
  },
  imageButtonImage: {
    width: '100%',
    height: '100%',
  },
  imageButtonText: {
    position: 'absolute',
    width: '100%',
    textAlign: 'center',
    color: '#F5F5DC',
    fontFamily: 'Sanchez',
    fontWeight: '500',
    fontSize: 20,
    textShadowColor: 'rgba(0, 0, 0, 0.45)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 3,
  },
  imageButtonTextDisabled: {
    color: '#C8C8B0',
  },
  defaultButtonSize: {
    width: 248,
    height: 58,
  },
  compactButtonSize: {
    width: 190,
    height: 50,
  },
});
